import { useMemo, useState } from 'react';
import { Search, SlidersHorizontal } from 'lucide-react';
import { useRouter } from '../router/MarketplaceRouter';
import { categories, services, getVendorById } from '../data/fixtures';
import { PageSection, ServiceCard } from '../shell/ui';

export function ServicesListScreen() {
  const { route, navigate } = useRouter();
  const initialCategory = route.params?.categoryId || 'all';
  const [query, setQuery] = useState('');
  const [cat, setCat] = useState<string>(initialCategory);
  const [sort, setSort] = useState<'rating' | 'price-asc' | 'price-desc'>('rating');

  const filtered = useMemo(() => {
    let result = services;
    if (cat !== 'all') result = result.filter((s) => s.categoryId === cat);
    if (query.trim()) {
      const q = query.toLowerCase();
      result = result.filter(
        (s) =>
          s.title.toLowerCase().includes(q) ||
          s.summary.toLowerCase().includes(q) ||
          s.tags.some((t) => t.includes(q)),
      );
    }
    if (sort === 'rating') result = [...result].sort((a, b) => b.rating - a.rating);
    if (sort === 'price-asc') result = [...result].sort((a, b) => a.priceFrom - b.priceFrom);
    if (sort === 'price-desc') result = [...result].sort((a, b) => b.priceFrom - a.priceFrom);
    return result;
  }, [query, cat, sort]);

  return (
    <>
      {/* Header + search */}
      <section
        style={{
          background: 'var(--color-background-surface)',
          borderBottom: '1px solid var(--color-border-light)',
          padding: '3rem 0 2.5rem',
        }}
      >
        <div style={{ maxWidth: '1240px', margin: '0 auto', padding: '0 1.5rem' }}>
          <div
            style={{
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.12em',
              color: 'var(--color-accent-rosegold)',
              fontWeight: 500,
              marginBottom: '0.875rem',
            }}
          >
            Services
          </div>
          <h1
            style={{
              fontFamily: 'var(--font-family-display)',
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: 400,
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              margin: 0,
              marginBottom: '1.75rem',
              maxWidth: '640px',
            }}
          >
            Every service, <span style={{ color: 'var(--color-accent-rosegold)' }}>side by side</span>.
          </h1>

          {/* Search */}
          <form
            onSubmit={(e) => e.preventDefault()}
            style={{
              display: 'flex',
              gap: '0.5rem',
              background: 'var(--color-background-card)',
              border: '1px solid var(--color-border-medium)',
              borderRadius: 'var(--radius-lg)',
              padding: '0.5rem 0.5rem 0.5rem 1.25rem',
              maxWidth: '620px',
              alignItems: 'center',
              boxShadow: 'var(--shadow-soft)',
            }}
          >
            <Search size={17} strokeWidth={1.75} style={{ color: 'var(--color-text-muted)' }} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by keyword, tag, or specialty"
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: '1rem',
                color: 'var(--color-text-primary)',
                fontFamily: 'var(--font-family-body)',
                minWidth: 0,
              }}
            />
            <button
              type="submit"
              style={{
                padding: '0.625rem 1.25rem',
                background: 'var(--color-accent-rosegold)',
                color: 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Search
            </button>
          </form>
        </div>
      </section>

      {/* Results layout with sidebar filter */}
      <PageSection paddingY="3rem">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '240px 1fr',
            gap: '2.5rem',
            alignItems: 'start',
          }}
          className="services-layout"
        >
          {/* Sidebar */}
          <aside
            style={{
              position: 'sticky',
              top: '100px',
              alignSelf: 'start',
            }}
            className="services-sidebar"
          >
            <div
              style={{
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                fontWeight: 600,
                color: 'var(--color-text-muted)',
                marginBottom: '0.875rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <SlidersHorizontal size={13} /> Category
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginBottom: '2rem' }}>
              {[{ id: 'all', name: 'All categories' }, ...categories].map((c) => {
                const isActive = cat === c.id;
                return (
                  <button
                    key={c.id}
                    onClick={() => setCat(c.id)}
                    style={{
                      all: 'unset',
                      cursor: 'pointer',
                      padding: '0.5rem 0.75rem',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: '0.9375rem',
                      color: isActive ? 'var(--color-accent-rosegold)' : 'var(--color-text-secondary)',
                      fontWeight: isActive ? 600 : 400,
                      background: isActive ? 'var(--color-accent-rosegold-50)' : 'transparent',
                    }}
                  >
                    {c.name}
                  </button>
                );
              })}
            </div>

            <div
              style={{
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                fontWeight: 600,
                color: 'var(--color-text-muted)',
                marginBottom: '0.875rem',
              }}
            >
              Sort
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              {(
                [
                  ['rating', 'Highest rated'],
                  ['price-asc', 'Price: low → high'],
                  ['price-desc', 'Price: high → low'],
                ] as const
              ).map(([value, label]) => {
                const isActive = sort === value;
                return (
                  <button
                    key={value}
                    onClick={() => setSort(value)}
                    style={{
                      all: 'unset',
                      cursor: 'pointer',
                      padding: '0.5rem 0.75rem',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: '0.9375rem',
                      color: isActive ? 'var(--color-accent-rosegold)' : 'var(--color-text-secondary)',
                      fontWeight: isActive ? 600 : 400,
                      background: isActive ? 'var(--color-accent-rosegold-50)' : 'transparent',
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </aside>

          {/* Results */}
          <div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1.5rem',
              }}
            >
              <div style={{ fontSize: '0.9375rem', color: 'var(--color-text-secondary)' }}>
                <strong style={{ color: 'var(--color-text-primary)' }}>{filtered.length}</strong>{' '}
                {filtered.length === 1 ? 'service' : 'services'}
              </div>
            </div>

            {filtered.length === 0 ? (
              <div
                style={{
                  padding: '3rem 2rem',
                  background: 'var(--color-background-card)',
                  border: '1px solid var(--color-border-light)',
                  borderRadius: 'var(--radius-lg)',
                  textAlign: 'center',
                }}
              >
                <p style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
                  No services match. Try a broader search or clear the filter.
                </p>
              </div>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '1.25rem',
                }}
                className="services-grid"
              >
                {filtered.map((s) => {
                  const vendor = getVendorById(s.vendorId)!;
                  return (
                    <ServiceCard
                      key={s.id}
                      image={s.heroImage}
                      title={s.title}
                      vendorName={vendor.name}
                      vendorAvatar={vendor.avatar}
                      rating={s.rating}
                      reviewCount={s.reviewCount}
                      priceFrom={s.priceFrom}
                      onClick={() => navigate('service', { serviceId: s.id })}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <style>{`
          @media (max-width: 1100px) {
            .services-grid { grid-template-columns: repeat(2, 1fr) !important; }
          }
          @media (max-width: 900px) {
            .services-layout { grid-template-columns: 1fr !important; }
            .services-sidebar { position: static !important; }
          }
          @media (max-width: 620px) {
            .services-grid { grid-template-columns: 1fr !important; }
          }
        `}</style>
      </PageSection>
    </>
  );
}
